import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowUp,
  ChevronDown,
  Paperclip,
  Pin,
  PinOff,
  Search,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import {
  ARKO_HUB_MODES_LEGACY,
  ARKO_INTENT_EVENT,
  dispatchArkoHubIntent,
  type ArkoHubMode,
} from './arkoHubModes';
import { ArkoComposerSearchChip } from './ArkoComposerSearchChip';
import type { ParagraphRef } from './portalParagraphRef';
import {
  COMPOSER_TOOL_PIN_CATALOG,
  getComposerPinMeta,
  isComposerPinId,
  loadComposerPins,
  modeIdToPinId,
  saveComposerPins,
  toggleComposerPin,
  type ComposerPinId,
  type ComposerToolPinId,
} from './portalComposerPins';
import { CURSOR_PORTAL } from '../arkorbit-ui/portalTheme';
import type { PortalChipBarPinControls } from '../arkorbit-ui/PortalChipBar';
import { PORTAL_DOCUMENT_FILE_ACCEPT } from './portalDocuments';
import {
  canPortalChatAnalyzeUpload,
  formatPortalFileSize,
  PORTAL_CHAT_ANALYZE_MAX_FILES,
  validatePortalChatAnalyzeFile,
} from './portalChatDocumentAttach';

export type ArkoCursorComposerHandle = {
  collapseTrays: () => void;
};

export interface ArkoCursorComposerProps {
  customerName?: string;
  kanzlei?: string;
  placeholder?: string;
  compact?: boolean;
  autoFocus?: boolean;
  /** `center` = leerer Chat; `bottom` = Composer am unteren Rand. */
  layout?: 'center' | 'bottom';
  /** Controlled-Modus für laufenden Chat (Intent-Chips, Draft). */
  value?: string;
  onChange?: (value: string) => void;
  onSubmit: (text: string, mode?: ArkoHubMode, attachments?: File[]) => void;
  onModeSelect?: (mode: ArkoHubMode) => void;
  /** Intent-Chips / Werkzeuge — z. B. PortalChipBar-Inhalt (wiederverwendet). */
  toolsBar?: React.ReactNode;
  showToolsTray?: boolean;
  /** Laufende ARKO-Anfrage — Senden-Button wird zu Abbrechen. */
  isLoading?: boolean;
  onCancel?: () => void;
  /** Angeheftete §§ aus der Paragraphen-Suchleiste (Kontext, kein Tray-Pin). */
  attachedParagraphs?: ParagraphRef[];
  onAttachedParagraphsChange?: (next: ParagraphRef[]) => void;
  /** Sofort-Nachschlagen über rechtsfrage / lookup-Pfad. */
  onParagraphLookup?: (refs: ParagraphRef[], prompt: string) => void;
  /** Verwandte §§ aus letztem lexProof.norm_chain (Suchleisten-Related). */
  relatedParagraphs?: ParagraphRef[];
  /**
   * Optional: User-ID für Pin-Prefs (localStorage-Key).
   * Ohne Session → Key `…:local`. Keine PII in den Prefs selbst.
   */
  pinsUserId?: string | null;
}

const TRAY_PILL =
  'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-slate-900/70 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:border-violet-500/30 hover:bg-violet-500/5 hover:text-slate-100 transition-colors';

const TRAY_PILL_ACTIVE =
  'inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-violet-400/25';

/**
 * ARKO-Composer — Cursor-Stil: Modus- + Werkzeuge-Reiter direkt im Composer.
 * Suchleiste (Paragraph suchen + §-Favoriten + Related) und Modus-/Tool-Pin-Tray parallel.
 * Verfahrensmotor Slice 1: strukturierte Bot-Antworten `messageKind: approval_frist`
 * werden in `InlineChat` als `FristApprovalCard` gerendert (nicht im Composer).
 */
export const ArkoCursorComposer = forwardRef<ArkoCursorComposerHandle, ArkoCursorComposerProps>(
  (
    {
      customerName,
      kanzlei,
      placeholder = 'Frag ARKO zu Recht, Text oder Dokumenten…',
      compact = false,
      autoFocus = true,
      layout = 'center',
      value: controlledValue,
      onChange: controlledOnChange,
      onSubmit,
      onModeSelect,
      toolsBar,
      showToolsTray = false,
      isLoading = false,
      onCancel,
      attachedParagraphs = [],
      onAttachedParagraphsChange,
      onParagraphLookup,
      relatedParagraphs = [],
      pinsUserId = null,
    },
    ref,
  ) => {
    const [internalText, setInternalText] = useState('');
    const text = controlledValue !== undefined ? controlledValue : internalText;
    const setText = controlledOnChange ?? setInternalText;
    const [activeModeId, setActiveModeId] = useState<string | null>(null);
    /** Werkzeug-Pin als Submit-Intent (Empty-State ohne InlineChat-pendingIntent). */
    const [activeToolMode, setActiveToolMode] = useState<ArkoHubMode | null>(null);
    const [openTray, setOpenTray] = useState<'modus' | 'werkzeuge' | null>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachError, setAttachError] = useState<string | null>(null);
    const [pinnedIds, setPinnedIds] = useState<ComposerPinId[]>(() =>
      loadComposerPins(pinsUserId),
    );
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const collapseTrays = useCallback(() => setOpenTray(null), []);

    useImperativeHandle(ref, () => ({ collapseTrays }), [collapseTrays]);

    useEffect(() => {
      setPinnedIds(loadComposerPins(pinsUserId));
    }, [pinsUserId]);

    const persistPins = useCallback(
      (next: ComposerPinId[]) => {
        setPinnedIds(next);
        saveComposerPins(next, pinsUserId);
      },
      [pinsUserId],
    );

    const handleTogglePin = useCallback(
      (id: ComposerPinId) => {
        persistPins(toggleComposerPin(pinnedIds, id));
      },
      [persistPins, pinnedIds],
    );

    const handleTogglePinFromBar = useCallback(
      (rawId: string) => {
        if (!isComposerPinId(rawId)) return;
        handleTogglePin(rawId);
      },
      [handleTogglePin],
    );

    useEffect(() => {
      if (!openTray) return;
      const onPointerDown = (e: MouseEvent | TouchEvent) => {
        const root = rootRef.current;
        if (root && !root.contains(e.target as Node)) {
          setOpenTray(null);
        }
      };
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
      return () => {
        document.removeEventListener('mousedown', onPointerDown);
        document.removeEventListener('touchstart', onPointerDown);
      };
    }, [openTray]);

    const activeMode = ARKO_HUB_MODES_LEGACY.find((m) => m.id === activeModeId);
    const isAnalyzeMode = activeModeId === 'analysieren';
    const canAttachFiles = isAnalyzeMode && canPortalChatAnalyzeUpload();
    const canSubmit = Boolean(text.trim()) || (isAnalyzeMode && attachments.length > 0);

    useEffect(() => {
      if (!isAnalyzeMode) {
        setAttachments([]);
        setAttachError(null);
      }
    }, [isAnalyzeMode]);

    const addAttachmentFiles = useCallback((fileList: FileList | null) => {
      if (!fileList?.length) return;
      setAttachError(null);
      const incoming = Array.from(fileList);
      setAttachments((prev) => {
        const next = [...prev];
        for (const file of incoming) {
          if (next.length >= PORTAL_CHAT_ANALYZE_MAX_FILES) {
            setAttachError(`Maximal ${PORTAL_CHAT_ANALYZE_MAX_FILES} Dateien pro Analyse.`);
            break;
          }
          const err = validatePortalChatAnalyzeFile(file);
          if (err) {
            setAttachError(err);
            continue;
          }
          if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
          next.push(file);
        }
        return next;
      });
    }, []);

    const removeAttachment = useCallback((index: number) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
      setAttachError(null);
    }, []);

    const handleMode = useCallback(
      (mode: ArkoHubMode) => {
        setActiveModeId(mode.id);
        setActiveToolMode(null);
        setOpenTray(null);
        onModeSelect?.(mode);
        dispatchArkoHubIntent(mode);
        const prefix = mode.promptPrefix ?? '';
        setText(text.trim() ? text : prefix);
        textareaRef.current?.focus();
      },
      [onModeSelect, setText, text],
    );

    const activateToolPin = useCallback(
      (pinId: ComposerToolPinId) => {
        const meta = getComposerPinMeta(pinId);
        if (!meta?.intent) return;
        const prefix = meta.promptPrefix ?? '';
        const toolMode: ArkoHubMode = {
          id: 'fragen',
          label: meta.label,
          description: meta.label,
          icon: ARKO_HUB_MODES_LEGACY[0]?.icon ?? Sparkles,
          intent: meta.intent,
          promptPrefix: prefix,
          accent: 'violet',
        };
        setActiveModeId(null);
        setActiveToolMode(toolMode);
        setOpenTray(null);
        setText(text.trim() ? text : prefix);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(ARKO_INTENT_EVENT, {
              detail: {
                intent: meta.intent,
                promptPrefix: prefix,
                modeId: 'fragen',
              },
            }),
          );
        }
        textareaRef.current?.focus();
      },
      [setText, text],
    );

    const activatePinned = useCallback(
      (pinId: ComposerPinId) => {
        const meta = getComposerPinMeta(pinId);
        if (!meta) return;
        if (meta.kind === 'mode' && meta.modeId) {
          const mode = ARKO_HUB_MODES_LEGACY.find((m) => m.id === meta.modeId);
          if (mode) handleMode(mode);
          return;
        }
        if (meta.kind === 'tool' && meta.intent) {
          activateToolPin(pinId as ComposerToolPinId);
        }
      },
      [activateToolPin, handleMode],
    );

    const toggleTray = useCallback((tray: 'modus' | 'werkzeuge') => {
      setOpenTray((prev) => (prev === tray ? null : tray));
    }, []);

    const handleSubmit = useCallback(
      (e?: React.FormEvent) => {
        e?.preventDefault();
        if (isLoading) return;
        const trimmed = text.trim();
        const filesToSend = isAnalyzeMode ? attachments : [];
        if (!trimmed && filesToSend.length === 0) return;
        const mode =
          activeToolMode ?? ARKO_HUB_MODES_LEGACY.find((m) => m.id === activeModeId);
        onSubmit(trimmed, mode ?? undefined, filesToSend.length > 0 ? filesToSend : undefined);
        if (controlledOnChange) controlledOnChange('');
        else setInternalText('');
        setAttachments([]);
        setAttachError(null);
        setActiveModeId(null);
        setActiveToolMode(null);
        setOpenTray(null);
      },
      [
        text,
        activeModeId,
        activeToolMode,
        attachments,
        isAnalyzeMode,
        onSubmit,
        controlledOnChange,
        isLoading,
      ],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        setOpenTray(null);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (!isLoading) handleSubmit();
      }
    };

    const pinControls: PortalChipBarPinControls = useMemo(
      () => ({
        pinnedIds,
        onTogglePin: handleTogglePinFromBar,
        schnellItems: COMPOSER_TOOL_PIN_CATALOG.filter((p) => p.toolsTab === 'schnell').map(
          (p) => ({ id: p.id, label: p.label }),
        ),
        rechtItems: COMPOSER_TOOL_PIN_CATALOG.filter((p) => p.toolsTab === 'recht').map((p) => ({
          id: p.id,
          label: p.label,
        })),
      }),
      [handleTogglePinFromBar, pinnedIds],
    );

    const toolsBarWithPins = useMemo(() => {
      if (!toolsBar || !isValidElement(toolsBar)) return toolsBar;
      return cloneElement(toolsBar as React.ReactElement<{ pinControls?: PortalChipBarPinControls }>, {
        pinControls,
      });
    }, [pinControls, toolsBar]);

    const firstName = customerName?.trim() || 'willkommen';
    const isBottom = layout === 'bottom';
    const showLanding = !isBottom && !compact;
    const hasTools = showToolsTray && Boolean(toolsBar);

    const trayPopoverClass =
      'absolute bottom-full left-0 mb-1.5 z-30 min-w-[min(100vw-2rem,20rem)] max-w-[min(100vw-2rem,28rem)] rounded-xl border border-white/[0.1] bg-[#141619] shadow-[0_-8px_32px_rgba(0,0,0,0.45)] overflow-hidden';

    const composerBox = (
      <form
        onSubmit={handleSubmit}
        className={`w-full ${isBottom ? '' : 'max-w-[min(100%,42rem)]'}`}
      >
        <div
          ref={rootRef}
          className={`${CURSOR_PORTAL.composer} ${CURSOR_PORTAL.composerFocus} transition-shadow ${
            compact || isBottom ? 'p-3' : 'p-4 md:p-5'
          }`}
        >
          <textarea
            ref={textareaRef}
            data-portal-arko-input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={compact || isBottom ? 2 : 3}
            autoFocus={autoFocus}
            className="w-full bg-transparent text-[#e6e7ea] text-[15px] leading-relaxed placeholder:text-slate-600 focus:outline-none resize-none min-h-[56px] max-h-[200px] overflow-y-auto custom-scrollbar"
          />

          {attachments.length > 0 ? (
            <ul
              className="flex flex-wrap gap-1.5 mt-2 max-h-28 overflow-y-auto custom-scrollbar portal-arko-composer-scroll"
              aria-label="Angehängte Dokumente"
            >
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${index}`}
                  className="inline-flex items-center gap-1.5 max-w-full rounded-lg border border-white/[0.08] bg-slate-900/60 px-2 py-1 text-[11px] text-slate-300"
                >
                  <span className="truncate max-w-[12rem]" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-slate-500 shrink-0">{formatPortalFileSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    disabled={isLoading}
                    className="p-0.5 rounded text-slate-500 hover:text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
                    aria-label={`${file.name} entfernen`}
                  >
                    <X className="w-3 h-3" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {attachError ? (
            <p className="mt-1.5 text-[11px] text-amber-400/90" role="alert">
              {attachError}
            </p>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={PORTAL_DOCUMENT_FILE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              addAttachmentFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {/* Cursor-Stil: Modus + Suchleiste + Pins + Werkzeuge */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-white/[0.06]">
            <div className="relative flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
              {/* Modus-Reiter */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => toggleTray('modus')}
                  aria-expanded={openTray === 'modus'}
                  aria-haspopup="listbox"
                  className={activeMode || activeToolMode ? TRAY_PILL_ACTIVE : TRAY_PILL}
                >
                  {activeMode
                    ? activeMode.label
                    : activeToolMode
                      ? activeToolMode.label
                      : 'Modus'}
                  <ChevronDown
                    className={`w-3 h-3 shrink-0 transition-transform ${
                      openTray === 'modus' ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
                {openTray === 'modus' ? (
                  <div className={trayPopoverClass} role="listbox" aria-label="ARKO-Modus wählen">
                    <div className="p-1.5 space-y-0.5 max-h-[min(60vh,320px)] overflow-y-auto custom-scrollbar">
                      {ARKO_HUB_MODES_LEGACY.map((mode) => {
                        const Icon = mode.icon;
                        const selected = activeModeId === mode.id;
                        const pinId = modeIdToPinId(mode.id);
                        const pinned = pinId ? pinnedIds.includes(pinId) : false;
                        return (
                          <div
                            key={mode.id}
                            className={`flex items-stretch gap-0.5 rounded-lg ${
                              selected ? CURSOR_PORTAL.chipActive : 'hover:bg-white/[0.04] text-slate-300'
                            }`}
                          >
                            <button
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => handleMode(mode)}
                              className="flex-1 min-w-0 text-left rounded-lg px-2.5 py-2 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 shrink-0 text-violet-400" aria-hidden />
                                <span className="text-xs font-medium text-slate-200">{mode.label}</span>
                              </div>
                              {!compact && (
                                <p className="text-[10px] text-slate-500 mt-0.5 pl-5 leading-snug line-clamp-2">
                                  {mode.description}
                                </p>
                              )}
                            </button>
                            {pinId ? (
                              <button
                                type="button"
                                onClick={() => handleTogglePin(pinId)}
                                aria-pressed={pinned}
                                aria-label={
                                  pinned
                                    ? `${mode.label} von Favoriten lösen`
                                    : `${mode.label} als Favorit anheften`
                                }
                                title={pinned ? 'Lösen' : 'Anheften'}
                                className="shrink-0 self-center mr-1.5 p-1.5 rounded-md text-slate-500 hover:text-violet-200 hover:bg-white/[0.06]"
                              >
                                {pinned ? (
                                  <PinOff className="w-3.5 h-3.5" aria-hidden />
                                ) : (
                                  <Pin className="w-3.5 h-3.5" aria-hidden />
                                )}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                      <p className="px-2.5 pt-1 pb-0.5 text-[10px] text-slate-600 leading-snug">
                        Paragraphen-Suche bleibt in der Suchleiste — kein zweites Analysieren.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Paragraphen-Suchleiste: immer sichtbar — Nachschlagen, §-Favoriten, Related */}
              <ArkoComposerSearchChip
                attached={attachedParagraphs}
                onAttachedChange={onAttachedParagraphsChange ?? (() => {})}
                onLookup={onParagraphLookup ?? (() => {})}
                relatedNorms={relatedParagraphs}
                disabled={isLoading || !onParagraphLookup || !onAttachedParagraphsChange}
              />

              {/* Angepinnte Modus-/Werkzeug-Favoriten (zusätzlich zur Suchleiste) */}
              {pinnedIds.map((pinId) => {
                const meta = getComposerPinMeta(pinId);
                if (!meta) return null;
                const selected =
                  (meta.kind === 'mode' && meta.modeId === activeModeId) ||
                  (meta.kind === 'tool' && meta.intent === activeToolMode?.intent);
                return (
                  <span
                    key={pinId}
                    className={`inline-flex items-center gap-0.5 rounded-full border pl-2.5 pr-0.5 py-0.5 text-[11px] font-medium ${
                      selected
                        ? 'border-violet-500/40 bg-violet-500/10 text-white ring-1 ring-violet-400/25'
                        : 'border-white/[0.08] bg-slate-900/70 text-slate-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => activatePinned(pinId)}
                      disabled={isLoading}
                      className="hover:text-white disabled:opacity-40"
                      aria-pressed={selected}
                      aria-label={`${meta.label} aktivieren`}
                    >
                      {meta.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTogglePin(pinId)}
                      className="p-1 rounded-full text-slate-500 hover:text-slate-200 hover:bg-white/[0.06]"
                      aria-label={`${meta.label} lösen`}
                      title="Lösen"
                    >
                      <X className="w-3 h-3" aria-hidden />
                    </button>
                  </span>
                );
              })}

              {/* Werkzeuge-Reiter */}
              {hasTools ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleTray('werkzeuge')}
                    aria-expanded={openTray === 'werkzeuge'}
                    aria-haspopup="true"
                    className={openTray === 'werkzeuge' ? TRAY_PILL_ACTIVE : TRAY_PILL}
                  >
                    Werkzeuge
                    <ChevronDown
                      className={`w-3 h-3 shrink-0 transition-transform ${
                        openTray === 'werkzeuge' ? 'rotate-180' : ''
                      }`}
                      aria-hidden
                    />
                  </button>
                  {openTray === 'werkzeuge' ? (
                    <div className={`${trayPopoverClass} min-w-[min(100vw-2rem,24rem)]`}>
                      <div className="p-2">{toolsBarWithPins}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {canAttachFiles ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || attachments.length >= PORTAL_CHAT_ANALYZE_MAX_FILES}
                  className={TRAY_PILL}
                  title="Dokumente einfügen (PDF, Word, …)"
                  aria-label="Dokumente einfügen"
                >
                  <Paperclip className="w-3 h-3 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Dokumente</span>
                </button>
              ) : null}
            </div>

            <div className="flex items-center gap-2 ml-auto shrink-0">
              <span className="text-[10px] text-slate-500 hidden sm:inline whitespace-nowrap">
                {isLoading ? 'Abbrechen' : 'Enter senden'}
              </span>
              {isLoading ? (
                <button
                  type="button"
                  onClick={() => onCancel?.()}
                  className={`flex items-center justify-center rounded-lg p-2 ${CURSOR_PORTAL.chipActive}`}
                  aria-label="Anfrage abbrechen"
                  title="Abbrechen"
                >
                  <Square className="w-4 h-4 fill-current" aria-hidden />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`flex items-center justify-center rounded-lg p-2 ${CURSOR_PORTAL.chipActive} disabled:opacity-30 disabled:cursor-not-allowed`}
                  aria-label="Nachricht senden"
                >
                  <ArrowUp className="w-4 h-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    );

    if (isBottom) {
      return (
        <div className="w-full max-w-[min(100%,48rem)] mx-auto px-3 sm:px-4">{composerBox}</div>
      );
    }

    return (
      <div
        className={`flex flex-col items-center justify-center w-full flex-1 min-h-0 ${
          compact ? 'px-2 py-3 gap-3' : 'px-4 py-6 md:py-10 gap-5'
        }`}
      >
        {showLanding && (
          <header className="text-center max-w-lg animate-fade-in flex-shrink-0">
            <div className="inline-flex items-center gap-2 mb-3 text-slate-500">
              <Sparkles className="w-4 h-4 text-violet-400" aria-hidden />
              <span className="text-[11px] font-medium uppercase tracking-wider">ARKO</span>
            </div>
            <h1 className="text-xl md:text-2xl font-medium text-white tracking-tight">
              Hallo, <span className="text-slate-400">{firstName}</span>
            </h1>
            {kanzlei ? <p className="text-sm text-slate-500 mt-1">{kanzlei}</p> : null}
          </header>
        )}

        <div className="w-full flex justify-center flex-shrink-0">{composerBox}</div>

        {!compact && (
          <p className="text-[11px] text-slate-500 text-center max-w-md leading-relaxed">
            Keine Rechtsberatung · Quellenpflicht bei Rechtsfragen · Daten bleiben in Ihrer Kanzlei
          </p>
        )}
      </div>
    );
  },
);

ArkoCursorComposer.displayName = 'ArkoCursorComposer';

/** Suchfeld für Cursor-Rail (Thread-Liste). */
export const ArkoCursorThreadSearch: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = 'Chats suchen…' }) => (
  <div className="relative">
    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" aria-hidden />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/[0.08] bg-slate-900/80 pl-8 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-violet-500/35"
    />
  </div>
);
